import axios, { type AxiosError, type AxiosResponse } from "axios";

export type ApiValidationError = {
  field: string;
  code: string;
  message: string;
};

export type ApiErrorBody = {
  code: string;
  message?: string;
  details?: Record<string, unknown>;
  correlationId: string;
  errors?: ApiValidationError[];
};

export type CleErrorKind =
  | "unauthorized"
  | "forbidden"
  | "not_found"
  | "conflict"
  | "validation"
  | "retry"
  | "generic";

export const ERROR_MESSAGES_DA: Record<string, string> = {
  DOCUMENT_ALREADY_EXISTS: "Dette dokument er allerede uploadet.",
  DOCUMENT_NOT_FOUND: "Dokumentet blev ikke fundet.",
  EVIDENCE_ALREADY_REPORTED:
    "Beviset er allerede indberettet for denne periode.",
  // The UI disables Godkend on an incomplete document, so reaching this code means the document
  // changed between the page loading and the click — another tab cleared a field, or the AI
  // re-analysed it. Say what to do rather than naming fields the stale page cannot show.
  EVIDENCE_FIELDS_INCOMPLETE:
    "Dokumentet mangler udfyldte felter og kan ikke godkendes. Genindlæs siden for at se, hvad der mangler.",
  EVIDENCE_NOT_FOUND: "Beviset blev ikke fundet.",
  FILE_TOO_LARGE: "Filen er for stor.",
  UNSUPPORTED_FILE_TYPE: "Filtypen understøttes ikke. Upload venligst en PDF.",
  VALIDATION_ERROR: "Ugyldige data. Ret felterne og prøv igen.",
  UNAUTHORIZED: "Du er ikke logget ind.",
  FORBIDDEN: "Du har ikke adgang til denne handling.",
  INTERNAL_ERROR: "Der opstod en fejl. Prøv igen senere.",
  SERVICE_UNAVAILABLE:
    "Tjenesten er midlertidigt utilgængelig. Prøv igen senere.",
  BATCH_LIMIT_EXCEEDED: "For mange filer i én upload.",
  UPLOAD_FAILED: "Upload af filen mislykkedes.",
  REVIEW_THREAD_NOT_FOUND: "Henvendelsen blev ikke fundet.",
  REVIEW_THREAD_CONFLICT: "Handlingen kan ikke udføres i den nuværende status.",
  DISPENSATION_ALREADY_EXISTS:
    "Dispensation for denne periode eksisterer allerede.",
  RECLASSIFY_STATUS_UNCHANGED: "Statusen kan ikke ændres til den samme status.",
  INVALID_USER: "Ugyldig bruger eller advokat.",
};

// The API collapses several distinct situations into a single REVIEW_THREAD_CONFLICT
// code and only tells them apart through the English `message`, so we match on a stable
// fragment of it. TODO: replace this with a machine-readable `details.reason` from
// cle-compliance-api (see ReviewThreadConflictError) so we stop depending on prose.
const REVIEW_THREAD_CONFLICT_MESSAGES_DA: ReadonlyArray<
  readonly [match: string, message: string]
> = [
  [
    "only the assigned admin can resolve",
    "Henvendelsen skal være tildelt dig, før du kan markere den som løst.",
  ],
  [
    "cannot assign a resolved review thread",
    "Henvendelsen er allerede løst og kan ikke tildeles.",
  ],
  [
    "cannot add admin comments to a resolved review thread",
    "Henvendelsen er løst. Du kan ikke tilføje flere kommentarer.",
  ],
  [
    "linked evidence is awaiting review",
    "Det tilknyttede bevis afventer stadig behandling. Behandl beviset, før du markerer henvendelsen som løst.",
  ],
  [
    "linked insurance policy is awaiting review",
    "Den tilknyttede forsikringspolice afventer stadig behandling. Behandl policen, før du markerer henvendelsen som løst.",
  ],
];

// The backend builds some of these messages from strings split across source lines, so
// normalise whitespace and case before matching.
const normalizeApiMessage = (message: string): string =>
  message.toLowerCase().replace(/\s+/g, " ").trim();

export const getReviewThreadConflictMessage = (
  message: string | undefined,
): string | undefined => {
  if (!message) return undefined;

  const normalized = normalizeApiMessage(message);
  return REVIEW_THREAD_CONFLICT_MESSAGES_DA.find(([match]) =>
    normalized.includes(match),
  )?.[1];
};

const GENERIC_VALIDATION_CODES_DA: Record<string, string> = {
  missing: "Feltet er påkrævet.",
  invalid: "Ugyldig værdi.",
  too_short: "Værdien er for kort.",
  too_long: "Værdien er for lang.",
  greater_than: "Værdien er for lav.",
  greater_than_equal: "Værdien er for lav.",
  less_than: "Værdien er for høj.",
  less_than_equal: "Værdien er for høj.",
};

export const FIELD_LABELS_DA: Record<string, string> = {
  numberOfLessons: "Antal lektioner",
  number_of_lessons: "Antal lektioner",
  courseTitle: "Kursustitel",
  course_title: "Kursustitel",
  courseProvider: "Kursusudbyder",
  course_provider: "Kursusudbyder",
  courseDate: "Kursusdato",
  course_date: "Kursusdato",
  courseEndDate: "Kursets slutdato",
  course_end_date: "Kursets slutdato",
  coursePeriod: "Kursusperiode",
  course_period: "Kursusperiode",
  // course_time is the total number of minutes across all lessons — a duration,
  // not a time of day.
  courseTime: "Kursets varighed",
  course_time: "Kursets varighed",
  signature: "Underskrift",
  providerLogo: "Udbyders logo",
  provider_logo: "Udbyders logo",
  courseDescription: "Kursusbeskrivelse",
  course_description: "Kursusbeskrivelse",
  isLegalCourseCertificate: "Juridisk kursusbevis",
  is_legal_course_certificate: "Juridisk kursusbevis",
  participantName: "Deltagernavn",
  participant_name: "Deltagernavn",
  insuranceCompany: "Forsikringsselskab",
  insurance_company: "Forsikringsselskab",
  policyStartDate: "Policestartdato",
  policy_start_date: "Policestartdato",
  publicationYear: "Udgivelsesår",
  publication_year: "Udgivelsesår",
  lawyerLiabilityAmount: "Advokatansvar",
  lawyer_liability_amount: "Advokatansvar",
  guaranteeAmount: "Garantibeløb",
  guarantee_amount: "Garantibeløb",
  file: "Fil",
  description: "Beskrivelse",
  reason: "Begrundelse",
  content: "Besked",
  externalEfteruddanelseperiodeId: "Efteruddannelsesperiode",
  external_efteruddanelseperiode_id: "Efteruddannelsesperiode",
  newStatus: "Status",
  new_status: "Status",
  userId: "Bruger",
  user_id: "Bruger",
  assigneeEmail: "Tildelt medarbejder",
  assignee_email: "Tildelt medarbejder",
};

export const FIELD_MESSAGES_DA: Record<string, string> = {
  "numberOfLessons.missing": "Antal lektioner er påkrævet.",
  "number_of_lessons.missing": "Antal lektioner er påkrævet.",
  "publicationYear.missing": "Udgivelsesår er påkrævet.",
  "publication_year.missing": "Udgivelsesår er påkrævet.",
  "lawyerLiabilityAmount.missing": "Advokatansvar er påkrævet.",
  "lawyer_liability_amount.missing": "Advokatansvar er påkrævet.",
  "guaranteeAmount.missing": "Garantibeløb er påkrævet.",
  "guarantee_amount.missing": "Garantibeløb er påkrævet.",
  "file.missing": "Fil er påkrævet.",
  "description.missing": "Beskrivelse er påkrævet.",
  "reason.missing": "Begrundelse er påkrævet.",
  "content.missing": "Besked er påkrævet.",
  "externalEfteruddanelseperiodeId.missing":
    "Efteruddannelsesperiode er påkrævet.",
  "external_efteruddanelseperiode_id.missing":
    "Efteruddannelsesperiode er påkrævet.",
};

const API_FIELD_TO_FORM_FIELD: Record<string, string> = {
  number_of_lessons: "numberOfLessons",
  publication_year: "publicationYear",
  lawyer_liability_amount: "lawyerLiabilityAmount",
  guarantee_amount: "guaranteeAmount",
  external_efteruddanelseperiode_id: "externalEfteruddanelseperiodeId",
  new_status: "newStatus",
  user_id: "userId",
  assignee_email: "assigneeEmail",
};

export const toFormFieldName = (apiField: string): string =>
  API_FIELD_TO_FORM_FIELD[apiField] ?? apiField;

export const getFieldUserMessage = (
  field: string,
  code: string,
): string | undefined =>
  FIELD_MESSAGES_DA[`${field}.${code}`] ?? GENERIC_VALIDATION_CODES_DA[code];

export const getUserMessage = (body: ApiErrorBody): string => {
  if (body.code === "REVIEW_THREAD_CONFLICT") {
    const specific = getReviewThreadConflictMessage(body.message);
    if (specific) return specific;
  }

  if (body.code === "VALIDATION_ERROR" && body.errors?.length) {
    const first = body.errors[0];
    return (
      getFieldUserMessage(first.field, first.code) ??
      ERROR_MESSAGES_DA.VALIDATION_ERROR
    );
  }

  return ERROR_MESSAGES_DA[body.code] ?? ERROR_MESSAGES_DA.INTERNAL_ERROR;
};

export const getValidationFieldErrors = (
  body: ApiErrorBody | null | undefined,
): Record<string, string> => {
  if (!body?.errors?.length) return {};

  return body.errors.reduce<Record<string, string>>((acc, err) => {
    const formField = toFormFieldName(err.field);
    acc[formField] =
      getFieldUserMessage(err.field, err.code) ??
      ERROR_MESSAGES_DA.VALIDATION_ERROR;
    return acc;
  }, {});
};

export const getCleErrorKind = (status: number, code: string): CleErrorKind => {
  if (status === 401 || code === "UNAUTHORIZED") return "unauthorized";
  if (status === 403 || code === "FORBIDDEN") return "forbidden";
  if (status === 404) return "not_found";
  if (status === 409) return "conflict";
  if (status === 422 || code === "VALIDATION_ERROR") return "validation";
  if (status === 500 || status === 503) return "retry";
  return "generic";
};

export const isApiErrorBody = (data: unknown): data is ApiErrorBody =>
  typeof data === "object" &&
  data !== null &&
  "code" in data &&
  typeof (data as ApiErrorBody).code === "string";

const fallbackBody = (correlationId = "unknown"): ApiErrorBody => ({
  code: "INTERNAL_ERROR",
  correlationId,
});

export class CleApiError extends Error {
  readonly status: number;
  readonly body: ApiErrorBody;
  readonly userMessage: string;
  readonly kind: CleErrorKind;

  constructor(status: number, body: ApiErrorBody) {
    super(body.code);
    this.name = "CleApiError";
    this.status = status;
    this.body = body;
    this.userMessage = getUserMessage(body);
    this.kind = getCleErrorKind(status, body.code);
  }
}

export const isCleApiError = (error: unknown): error is CleApiError =>
  error instanceof CleApiError;

const parseBodyFromData = (data: unknown): ApiErrorBody => {
  if (isApiErrorBody(data)) {
    return {
      ...data,
      correlationId: data.correlationId ?? "unknown",
    };
  }

  return fallbackBody();
};

export const parseCleApiError = (error: unknown): CleApiError | null => {
  if (isCleApiError(error)) return error;

  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError;
    const status = axiosError.response?.status ?? 500;
    const body = parseBodyFromData(axiosError.response?.data);
    return new CleApiError(status, body);
  }

  if (
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    "data" in error
  ) {
    const response = error as AxiosResponse;
    const body = parseBodyFromData(response.data);
    return new CleApiError(response.status ?? 500, body);
  }

  return null;
};

export const logCleApiError = (error: unknown): void => {
  const cleError = parseCleApiError(error);
  if (!cleError) {
    console.error("CLE API error (unparseable)", error);
    return;
  }

  console.error(
    cleError.body.code,
    cleError.body.correlationId,
    cleError.body.message,
  );
};

export const getCleUserMessage = (
  error: unknown,
  fallback = ERROR_MESSAGES_DA.INTERNAL_ERROR,
): string => parseCleApiError(error)?.userMessage ?? fallback;

export const applyCleValidationErrorsToFormik = (
  error: unknown,
  setFieldError: (field: string, message: string | undefined) => void,
): void => {
  const fieldErrors = getValidationFieldErrors(parseCleApiError(error)?.body);
  Object.entries(fieldErrors).forEach(([field, message]) =>
    setFieldError(field, message),
  );
};

export const parseCleApiErrorBody = async (
  _status: number,
  data: unknown,
  contentType?: string,
): Promise<ApiErrorBody> => {
  if (data instanceof Blob) {
    const isJson =
      contentType?.includes("application/json") ||
      contentType?.includes("application/problem+json");

    if (isJson) {
      try {
        const text = await data.text();
        const parsed = JSON.parse(text) as unknown;
        if (isApiErrorBody(parsed)) {
          return {
            ...parsed,
            correlationId: parsed.correlationId ?? "unknown",
          };
        }
      } catch {
        return fallbackBody();
      }
    }

    return fallbackBody();
  }

  return parseBodyFromData(data);
};

export const toCleApiError = async (
  error: AxiosError,
): Promise<CleApiError> => {
  const status = error.response?.status ?? 500;
  const contentType = error.response?.headers?.["content-type"];
  const body = await parseCleApiErrorBody(
    status,
    error.response?.data,
    typeof contentType === "string" ? contentType : undefined,
  );
  return new CleApiError(status, body);
};
